import React from 'react';

import {
  useDeleteCampaignMutation,
  useFundraiseActivityQuery,
  useMarkCampaignCompleteMutation,
  useReopenCampaignMutation,
  useSaveInvestorCardMutation as useBaseSaveInvestorCardMutation,
  SaveInvestorCardInput,
  FundraiseActivityDocument
} from 'generated/graphql';
import _ from 'lodash';
import { useLoading } from 'modules';
import moment from 'moment';

import * as types from './types';



export function useFundraiseActivity(id: string) {
  const tuple = useFundraiseActivityQuery({ variables: { id } });
  const [markCampaignComplete, markCampaignCompleteTuple] = __useMarkCampaignComplete(id);
  const [reopenCampaign, reopenCampaignTuple] = __useReopenCampaign(id);
  const [deleteCampaign, deleteCampaignTuple] = __useDeleteCampaign(id);
  const loading = useLoading([tuple, markCampaignCompleteTuple, reopenCampaignTuple, deleteCampaignTuple]);

  const data = React.useMemo((): types.IData => {
    const campaign = tuple.data?.campaign;

    const investorIds = new Set(_.compact(_.map(tuple.data?.campaign?.investorCards, card => card.investorProfile?.id)));
    const favoritedInvestors = _.filter(tuple.data?.favoritedInvestors, investor => !investorIds.has(investor.investorProfile?.id ?? ''));

    const stageName2Cards = _.chain(tuple.data?.campaign?.investorCards)
      .groupBy(card => card.stage.name)
      .mapValues(cards => _.map(cards, (c): types.IInvestorCard => ({
        id: c.id,
        displayName: c.displayName,
        potentialInvestmentAmountDollars: c.potentialInvestmentAmountDollars,
        currentStageName: c.stage.name,
        notes: _.map(c.notes, (note): types.INote => ({
          id: note.id,
          title: note.title,
          body: note.body
        })) ?? []
      })))
      .value();

    return ({
      stages: _.map(tuple.data?.actorStages ?? [], (stage): types.IStage => ({
        name: stage.name,
        displayName: stage.overrideDisplayName || stage.displayName
      })),
      favoritedInvestors: _.map(favoritedInvestors ?? [], investor => ({
        id: investor.id,
        investorId: investor.investorProfile?.id ?? '',
        companyProfileId: investor.companyProfile?.id ?? '',
        companyName: investor.companyProfile?.name ?? ''
      })),
      campaign: {
        id: campaign?.id ?? '',
        name: campaign?.name ?? '',
        description: campaign?.description ?? '',
        stageDisplayName: campaign?.fundingStage.displayName ?? '',
        targetCloseDate: campaign?.targetCloseDate ? moment(campaign?.targetCloseDate, moment.ISO_8601) : moment(),
        completedAt: campaign?.completedAt ? moment(campaign.completedAt, moment.ISO_8601) : null,
        amountRaisedDollars: campaign?.amountRaisedDollars ?? 0,
        targetAmountDollars: campaign?.targetAmountDollars ?? 0
      },
      stageName2InvestorCards: {
        committed: [],
        dataRoomAccess: [],
        favorites: [],
        followedUp: [],
        inDiligence: [],
        initialMeeting: [],
        passed: [],
        reachedOut: [],
        ...stageName2Cards
      }
    });
  }, [tuple.data]);

  return {
    loading,
    data,
    mutations: {
      markCampaignComplete,
      reopenCampaign,
      deleteCampaign
    },
    refetch: () => tuple.refetch({ id })
  };
}



function __useMarkCampaignComplete(id: string) {
  const [fnMutate, tuple] = useMarkCampaignCompleteMutation();

  async function fnWrappedMutate() {
    await fnMutate({ variables: { id } });
  }

  return [fnWrappedMutate, tuple] as const;
}



function __useReopenCampaign(id: string) {
  const [fnMutate, tuple] = useReopenCampaignMutation();

  async function fnWrappedMutate() {
    await fnMutate({ variables: { id } });
  }

  return [fnWrappedMutate, tuple] as const;
}



function __useDeleteCampaign(id: string) {
  const [fnMutate, tuple] = useDeleteCampaignMutation();

  async function fnWrappedMutate() {
    await fnMutate({ variables: { id } });
  }

  return [fnWrappedMutate, tuple] as const;
}



export function useSaveInvestorCardMutation() {
  const [fnRunMutation, saveInvestorCardMutationTuple] = useBaseSaveInvestorCardMutation({
    refetchQueries: [
      FundraiseActivityDocument,
      'FundraiseActivity'
    ]
  });

  const fnWrappedSaveInvestorCardMutation = async (input: SaveInvestorCardInput) =>
    fnRunMutation({
      variables: {
        input
      }
    });

  return [fnWrappedSaveInvestorCardMutation, saveInvestorCardMutationTuple] as const;
}
